import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";

import { checkIsPaidPlan } from "@degaus/shared/plans";

import { FullScreenLoader } from "@/components/FullScreenLoader";
import { ensureGatewayIdentity } from "@/hooks/useSession";
import { stewardFontLinks } from "@/landing/styles/steward-fonts";
import stewardThemeCss from "@/landing/styles/steward-theme.css?url";

// Reachable WITHOUT paid access so a gated user can pay, manage their account,
// mint a CLI key, or run onboarding (which contains the subscribe step). The
// /account shell stays open for subscription and security recovery; product
// data requested inside it (for example credit balance and transactions) is
// still gated server-side. Matched against the URL pathname (the (wonda) group
// is pathless).
const PAYWALL_ALLOWLIST = [
  "/cli-auth",
  "/authorize",
  "/link",
  "/download",
  "/setup",
  "/account",
  "/onboarding",
];
// Carved OUT of the /onboarding allowlist: the guide is the post-subscribe
// destination, not part of subscribing. Leaving it exempt would let an enforced
// unpaid user with saved welcome answers open it directly and finish onboarding
// past the non-skippable paywall step, so it stays gated like a product route.
const PAYWALL_ALLOWLIST_EXCLUSIONS = ["/onboarding/guide"];

// Routes exempt from the not-onboarded gate: the onboarding flow itself,
// /account (it runs its own guard with a FRESH identity read, which also keeps
// the query cache honest right after completeWelcome), the OAuth/CLI
// handshake routes — forcing onboarding mid-handshake would break CLI logins —
// and /download (any signed-in account may grab the Mac app installer without
// completing onboarding first).
const ONBOARDING_GATE_EXEMPT = [
  "/onboarding",
  "/account",
  "/authorize",
  "/oauth/consent",
  "/cli-auth",
  "/link",
  "/download",
];

export const Route = createFileRoute("/_authenticated/(wonda)/_wonda")({
  // Web paywall guard. The server gate is the real enforcement; this redirects
  // unpaid users away from product pages whose calls would all return 403.
  beforeLoad: async ({ context, location, preload }) => {
    const path = location.pathname;
    const matchesPrefix = (prefix: string) =>
      path === prefix || path.startsWith(`${prefix}/`);

    // Not-onboarded gate: a signed-in user who never completed onboarding is
    // funneled back into it from ANY product route (the landing layout does
    // the same client-side). Runs before the paywall gate since onboarding
    // contains the subscribe step. Fails open when identity can't be resolved
    // (provisioning, userinfo blip) — the /account guard owns those states.
    // skip_welcome mirrors the /account escape hatch. Skipped on preload:
    // redirects there are wasted work, the real navigation re-runs this.
    if (
      !preload &&
      !ONBOARDING_GATE_EXEMPT.some(matchesPrefix) &&
      !location.searchStr.includes("skip_welcome")
    ) {
      const identity = await ensureGatewayIdentity();
      if (
        identity?.publicMetadata.accountId &&
        !identity.publicMetadata.onboarded
      ) {
        throw redirect({ to: "/onboarding" });
      }
    }

    if (
      PAYWALL_ALLOWLIST.some(matchesPrefix) &&
      !PAYWALL_ALLOWLIST_EXCLUSIONS.some(matchesPrefix)
    ) {
      return;
    }

    let features;
    try {
      features =
        await context.trpcQueryUtils.payments.getAccountFeatures.ensureData();
    } catch {
      // Never lock a user out on a transient entitlement-fetch error.
      return;
    }

    if (features.hasPaidAccess) return;

    // Org members may be entitled via a paid org seat the personal-scope
    // getAccountFeatures can't see, so don't bounce them to a personal
    // subscribe page. Mirrors the onboarding paywall org-skip; best-effort.
    const orgs = await context.trpcQueryUtils.organization.list
      .ensureData()
      .catch(() => "lookup-failed" as const);
    // Fail open on a transient org-lookup error: never lock out a member who
    // may be entitled via an org seat.
    if (orgs === "lookup-failed") return;
    if (orgs.some((org) => checkIsPaidPlan(org.seatPlanCode))) return;

    // Final check against a stale cache: the ensureData read above can lag a
    // just-completed subscribe by up to the query stale window while the server
    // already treats the account as paid. Refetch fresh once and bail if
    // entitlement now clears, so a paid user is never bounced to the subscribe
    // page. Fail open on a fetch error.
    const fresh = await context.trpcQueryUtils.payments.getAccountFeatures
      .fetch(undefined, { staleTime: 0 })
      .catch(() => null);
    if (!fresh || fresh.hasPaidAccess) {
      return;
    }

    // Unpaid with no paid org seat: send to the subscribe page.
    // /account is allowlisted above (no redirect loop); reaching the subscribe
    // UI requires the wondaCliPlansEnabled flag ON in prod (a launch prereq).
    throw redirect({ to: "/account/plan" });
  },
  component: WondaLayout,
  pendingComponent: FullScreenLoader,
  head: () => ({
    links: [{ rel: "stylesheet", href: stewardThemeCss }, ...stewardFontLinks],
  }),
});

function WondaLayout() {
  return (
    // `steward-theme` scopes the light tokens; the ground itself is painted by
    // the global html/body base in tailwind.css.
    <div className="steward-theme flex min-h-[100dvh] flex-col">
      <Outlet />
    </div>
  );
}
